"use client"

import { useRouter } from "@bprogress/next/app";
import { Box, colors, Typography } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import CenterBox from "@v2/components/styled/CenterBox";
import { forwardRef, MouseEvent, PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pruneParamsForCategory } from "../categorySwitch";
import { PLPFilterProductClass, PLPSearchParams } from "../types";
import { requestPlpHeaderScroll } from "./scrollToPlpHeader";

const CategoryCard = forwardRef<HTMLDivElement, {
    productClass: PLPFilterProductClass,
    isSelected: boolean,
    onClick?: () => void,
}>(function CategoryCard({
    productClass,
    isSelected,
    onClick,
}, ref) {
    return (
        <Box sx={{
            bgcolor: '#fff',
            borderRadius: {
                xs: 1.2,
                md: 2
            },
            overflow: 'hidden',
            userSelect: 'none',
            cursor: onClick ? 'pointer' : 'default',
            position: 'relative',
            aspectRatio: '1 / 1',
            width: '100%',
            '&:hover': onClick ? {
                '& .prd_cls_img': {
                    filter: 'grayscale(0.4)',
                },
                '& .prd_cls_title': {
                    '& > span': {
                        color: isSelected ? "#fff" : colors.grey[900],
                    },
                }
            } : {},
            ...(isSelected ? {
                '& .prd_cls_img': {
                    filter: 'grayscale(0.1) !important',
                },
            } : {}),
        }}
            ref={ref}
            onClick={onClick}
            onDragStart={(event) => event.preventDefault()}
        >
            <CenterBox sx={{
                width: '100%',
                aspectRatio: '1 / 0.8'
            }}>
                <MediaV2 media_id={productClass.media_id}
                    alt={productClass.site_name}
                    height={234}
                    imageProps={{
                        draggable: false,
                        priority: true,
                        fill: true, style: {
                            objectFit: 'cover'
                        }
                    }} options={{
                        quality: 80
                    }} boxProps={{
                        className: 'prd_cls_img',
                        sx: {
                            filter: 'grayscale(1)',
                            transition: '120ms',
                            height: '100%',
                            aspectRatio: '1 / 0.8',
                        }
                    }} />
            </CenterBox>
            <CenterBox className="prd_cls_title" sx={{
                position: 'absolute',
                bottom: 0, right: 0, left: 0,
                width: '100%',
                aspectRatio: '1 / 0.2',
                overflow: 'hidden',
                bgcolor: colors.grey[300],
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    bgcolor: isSelected ? colors.grey[900] : 'transparent',
                },
            }}>
                <Typography component="span" sx={{
                    transition: '200ms',
                    zIndex: 1,
                    fontWeight: 500,
                    fontSize: {
                        xs: 12,
                        md: 16
                    },
                    color: isSelected ? '#fff' : colors.grey[700]
                }}>
                    {productClass.site_name}
                </Typography>
            </CenterBox>
        </Box>
    );
});

export default function PlpTopCategories(props: {
    data: PLPFilterProductClass[],
    searchParams: PLPSearchParams,
    currentPath?: string,
}) {
    const router = useRouter()
    const [pendingProductClassSlug, setPendingProductClassSlug] = useState<string | null>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const dragStateRef = useRef({
        isDragging: false,
        startX: 0,
        startScrollLeft: 0,
        didDrag: false,
        suppressClick: false,
    });
    const snapRestoreTimerRef = useRef(0);
    const snapRestoreHandlerRef = useRef<(() => void) | null>(null);
    const selectedCardRef = useRef<HTMLDivElement>(null);
    const detachDragListenersRef = useRef<(() => void) | null>(null);

    const currentProductClassSlug = Array.isArray(props.searchParams.product_class_slug)
        ? [...props.searchParams.product_class_slug].reverse()[0]
        : props.searchParams.product_class_slug;
    const currentPathname = props.currentPath
        ? new URL(props.currentPath, process.env.NEXT_PUBLIC_HOME_URL ?? "https://vahdatoptic.com").pathname.replace(/\/$/, "")
        : null;
    const currentProductClass: PLPFilterProductClass | undefined = props.data.find(i => i.slug === currentProductClassSlug)
        ?? props.data.find(i => i.link && new URL(i.link, process.env.NEXT_PUBLIC_HOME_URL ?? "https://vahdatoptic.com").pathname.replace(/\/$/, "") === currentPathname);

    const productClasses = useMemo(() => {
        const childProductClasses = props.data.filter(i => i.parent_id === Number(currentProductClass?.id));

        if (currentProductClass && childProductClasses.length) {
            return [currentProductClass, ...childProductClasses];
        }

        if (currentProductClass?.parent_id) {
            const parentProductClass = props.data.find(i => Number(i.id) === currentProductClass.parent_id);
            const childProductClasses = props.data.filter(i => i.parent_id === Number(parentProductClass?.id));

            return parentProductClass ? [parentProductClass, ...childProductClasses] : childProductClasses;
        }

        return props.data.filter(i => !i.parent_id);
    }, [currentProductClass, props.data])

    useEffect(() => {
        setPendingProductClassSlug(null);
    }, [currentProductClassSlug, props.currentPath])

    const onPickProductClass = useCallback((prdCls: PLPFilterProductClass) => () => {
        if (prdCls.id === currentProductClass?.id) return;

        setPendingProductClassSlug(prdCls.slug);

        const current = new URLSearchParams(Object.entries(props.searchParams));
        current.delete('slug')
        current.delete('brand_slug')
        current.delete('collection_slug')
        current.delete('product_class_slug')
        current.delete('lp')
        current.delete('page')

        // Filters used to survive every switch, so picking a category that shares
        // none of them landed on an empty list. Only what is still valid for the
        // target is carried over now.
        pruneParamsForCategory(current, prdCls)

        if (props.searchParams.sort) {
            current.set('sort', props.searchParams.sort)
        }

        // if (!prdCls.link) {
            current.set('product_class_slug', prdCls.slug)
        // }
        // router.push(prdCls.link ? `${prdCls.link}?${current.toString()}` : `/fa/products?${current.toString()}`);
        // Picking a category reloads the strip with the level below it, so the page
        // comes to rest on the strip itself: the shopper sees the choices they just
        // opened, with the first row of the new list under them.
        requestPlpHeaderScroll('categories');
        router.push(`/fa/products?${current.toString()}`, { scroll: false });
    }, [currentProductClass?.id, props.searchParams, props.searchParams.sort, router])

    // a pending restore must never fire into a new drag, so it is always detachable
    const clearPendingSnapRestore = useCallback(() => {
        window.clearTimeout(snapRestoreTimerRef.current);

        if (snapRestoreHandlerRef.current) {
            scrollerRef.current?.removeEventListener('scrollend', snapRestoreHandlerRef.current);
            snapRestoreHandlerRef.current = null;
        }
    }, []);

    // mandatory snapping fights a manual drag, so it is off while the pointer is down
    const setScrollSnapEnabled = useCallback((enabled: boolean) => {
        clearPendingSnapRestore();

        if (!scrollerRef.current) return;
        scrollerRef.current.style.scrollSnapType = enabled ? '' : 'none';
    }, [clearPendingSnapRestore]);

    useEffect(() => () => {
        clearPendingSnapRestore();
        detachDragListenersRef.current?.();
    }, [clearPendingSnapRestore])

    // land on the closest card smoothly, then hand the scroller back to css snapping
    const snapToNearestCard = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
        let nearestDelta: number | null = null;

        for (const card of Array.from(scroller.children) as HTMLElement[]) {
            const cardRect = card.getBoundingClientRect();
            const delta = cardRect.left + cardRect.width / 2 - scrollerCenter;

            if (nearestDelta === null || Math.abs(delta) < Math.abs(nearestDelta)) {
                nearestDelta = delta;
            }
        }

        if (nearestDelta === null) {
            setScrollSnapEnabled(true);
            return;
        }

        const restoreScrollSnap = () => {
            // a new drag already took over, it owns the snap state now
            if (dragStateRef.current.isDragging) return;

            setScrollSnapEnabled(true);
        };

        scroller.scrollBy({ left: nearestDelta, behavior: 'smooth' });
        snapRestoreHandlerRef.current = restoreScrollSnap;
        scroller.addEventListener('scrollend', restoreScrollSnap);
        // scrollend is not everywhere yet, so the timeout closes the gap
        snapRestoreTimerRef.current = window.setTimeout(restoreScrollSnap, 500);
    }, [setScrollSnapEnabled]);

    // the selected category can sit outside the viewport on load, so bring it in
    useEffect(() => {
        const scroller = scrollerRef.current;
        const selectedCard = selectedCardRef.current;
        if (!scroller || !selectedCard) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const cardRect = selectedCard.getBoundingClientRect();
        if (cardRect.left >= scrollerRect.left && cardRect.right <= scrollerRect.right) return;

        const delta = cardRect.left + cardRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);
        scroller.scrollBy({ left: delta, behavior: 'smooth' });
    }, [currentProductClass?.id, productClasses])

    const stopDragging = useCallback(() => {
        detachDragListenersRef.current?.();

        if (!dragStateRef.current.isDragging) return;

        dragStateRef.current.isDragging = false;
        dragStateRef.current.suppressClick = dragStateRef.current.didDrag;

        if (scrollerRef.current) {
            scrollerRef.current.style.cursor = '';
        }

        // a plain click must not re-center the row, only a real drag snaps
        if (dragStateRef.current.didDrag) {
            snapToNearestCard();
        } else {
            setScrollSnapEnabled(true);
        }
    }, [setScrollSnapEnabled, snapToNearestCard]);

    const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
        const scroller = scrollerRef.current;
        // touch devices keep the native scroll, only the mouse needs a manual drag
        if (event.pointerType !== 'mouse' || !scroller) return;

        setScrollSnapEnabled(false);
        // a smooth snap may still be running, stop it before reading the start offset
        scroller.scrollTo({ left: scroller.scrollLeft, behavior: 'instant' });

        dragStateRef.current = {
            isDragging: true,
            startX: event.clientX,
            startScrollLeft: scroller.scrollLeft,
            didDrag: false,
            suppressClick: false,
        };

        const onWindowPointerMove = (moveEvent: globalThis.PointerEvent) => {
            const deltaX = moveEvent.clientX - dragStateRef.current.startX;

            if (Math.abs(deltaX) > 4 && !dragStateRef.current.didDrag) {
                dragStateRef.current.didDrag = true;
                // the cursor is set on the node, a re-render here would stutter the drag
                scroller.style.cursor = 'grabbing';
            }

            scroller.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
        };

        // the drag follows the window instead of taking pointer capture, because
        // capture retargets the click to the scroller and the card never gets it
        window.addEventListener('pointermove', onWindowPointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);

        detachDragListenersRef.current = () => {
            window.removeEventListener('pointermove', onWindowPointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
            detachDragListenersRef.current = null;
        };
    }, [setScrollSnapEnabled, stopDragging]);

    // a drag that ends on a card must not navigate to it
    const suppressClickAfterDrag = useCallback((event: MouseEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        dragStateRef.current.suppressClick = false;
    }, []);

    return <Box sx={{
        bgcolor: colors.grey[200],
        overflow: 'hidden',
    }}>
        <Box
            dir="rtl"
            ref={scrollerRef}
            onPointerDown={onPointerDown}
            onClickCapture={suppressClickAfterDrag}
            sx={{
                '--plp-top-category-size': { xs: '148px', sm: '234px' },
                width: '100%',
                boxSizing: 'border-box',
                py: { xs: 2, sm: 3 },
                px: {
                    xs: 2,
                    sm: 3,
                    lg2: 'max(0px, calc((100vw - 1400px) / 2))',
                },
                display: 'flex',
                justifyContent: 'flex-start',
                gap: { xs: 2, sm: 3 },
                overflowX: 'auto',
                overflowY: 'hidden',
                overscrollBehaviorX: 'contain',
                scrollbarWidth: 'none',
                scrollSnapType: 'x mandatory',
                touchAction: 'pan-x pan-y',
                cursor: 'grab',
                userSelect: 'none',
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                '& img': {
                    userSelect: 'none',
                    pointerEvents: 'none',
                    WebkitUserDrag: 'none',
                },
            }}
            onDragStart={(event) => event.preventDefault()}
        >
            {productClasses.map((productClass) => {
                const isCurrent = currentProductClass?.id === productClass.id;
                const isSelected = isCurrent || pendingProductClassSlug === productClass.slug;
                return (
                    <Box
                        key={productClass.id}
                        ref={isCurrent ? selectedCardRef : undefined}
                        sx={{
                            flexShrink: 0,
                            width: 'var(--plp-top-category-size)',
                            minWidth: 'var(--plp-top-category-size)',
                            height: 'var(--plp-top-category-size) !important',
                            scrollSnapAlign: 'center',
                        }}
                    >
                        <CategoryCard productClass={productClass} isSelected={isSelected} onClick={onPickProductClass(productClass)} />
                    </Box>
                )
            })}
        </Box>
    </Box>
}
